import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  Cell,
} from 'recharts';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PercentIcon from '@mui/icons-material/Percent';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const CORES_GRUPOS = [
  '#1E3A5F', '#2D7D9A', '#27AE60', '#F39C12', '#E74C3C',
  '#8E44AD', '#E67E22', '#2C3E50', '#16A085', '#C0392B',
];

const SERIE_LABELS: Record<string, string> = {
  total_icsaps: 'ICSAPS',
  total_internacoes: 'Total internações',
  taxa_por_10k: 'Taxa / 10k hab',
  pct_icsaps: '% ICSAPS',
  custo_total: 'Custo (R$)',
};

const fmtCount = (value: unknown, label: string): [string, string] => [
  Number(value ?? 0).toLocaleString('pt-BR'),
  label,
];

const fmtSerie = (value: unknown, name: unknown): [string, string] => {
  const key = String(name ?? '');
  return [
    Number(value ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 }),
    SERIE_LABELS[key] || key,
  ];
};

const fmtPiramide = (value: unknown, name: unknown): [string, string] => {
  const key = String(name ?? '');
  return [
    Math.abs(Number(value ?? 0)).toLocaleString('pt-BR'),
    key === 'masculino' ? 'Masculino' : 'Feminino',
  ];
};

interface IcsapsFiltros {
  segmento: string;
  ano: string;
  mes: string;
  cid: string;
  grupo_icsap: string;
  codigo_unidade: string;
  sexo: string;
  faixa_etaria: string;
  raca: string;
}

const filtrosVazios: IcsapsFiltros = {
  segmento: 'todos',
  ano: '',
  mes: '',
  cid: '',
  grupo_icsap: '',
  codigo_unidade: '',
  sexo: '',
  faixa_etaria: '',
  raca: '',
};

const MonitoramentoIcsaps: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pacientes, setPacientes] = useState<any[]>([]);
  const [cidSelecionado, setCidSelecionado] = useState<string | null>(null);
  const [loadingPacientes, setLoadingPacientes] = useState(false);

  const hoje = new Date();
  const dozeMesesAtras = new Date();
  dozeMesesAtras.setMonth(dozeMesesAtras.getMonth() - 12);

  const [filtroInicio, setFiltroInicio] = useState(toISO(dozeMesesAtras));
  const [filtroFim, setFiltroFim] = useState(toISO(hoje));
  const [dataInicio, setDataInicio] = useState(toISO(dozeMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));
  const [filtros, setFiltros] = useState<IcsapsFiltros>(filtrosVazios);
  const [filtrosAplicados, setFiltrosAplicados] = useState<IcsapsFiltros>(filtrosVazios);

  const handleDateChange = useCallback((di: string, df: string) => {
    setFiltroInicio(di);
    setFiltroFim(df);
  }, []);

  const handleBuscar = useCallback(() => {
    setDataInicio(filtroInicio);
    setDataFim(filtroFim);
    setFiltrosAplicados({ ...filtros });
    setCidSelecionado(null);
    setPacientes([]);
  }, [filtroInicio, filtroFim, filtros]);

  const filtrosParams = useMemo(() => {
    const p: Record<string, string | undefined> = {};
    if (filtrosAplicados.segmento && filtrosAplicados.segmento !== 'todos') {
      p.segmento = filtrosAplicados.segmento;
    }
    if (filtrosAplicados.ano) p.ano = filtrosAplicados.ano;
    if (filtrosAplicados.mes) p.mes = filtrosAplicados.mes;
    if (filtrosAplicados.cid) p.cid = filtrosAplicados.cid;
    if (filtrosAplicados.grupo_icsap) p.grupo_icsap = filtrosAplicados.grupo_icsap;
    if (filtrosAplicados.codigo_unidade) p.codigo_unidade = filtrosAplicados.codigo_unidade;
    if (filtrosAplicados.sexo) p.sexo = filtrosAplicados.sexo;
    if (filtrosAplicados.faixa_etaria) p.faixa_etaria = filtrosAplicados.faixa_etaria;
    if (filtrosAplicados.raca) p.raca = filtrosAplicados.raca;
    return p;
  }, [filtrosAplicados]);

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getIcsaps(
          municipioAtivo.id,
          dataInicio,
          dataFim,
          filtrosParams,
        );
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de ICSAPS.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim, filtrosParams]);

  const carregarPacientes = async (cid: string) => {
    if (!municipioAtivo?.id) return;
    if (cidSelecionado === cid) {
      setCidSelecionado(null);
      setPacientes([]);
      return;
    }
    setCidSelecionado(cid);
    setLoadingPacientes(true);
    try {
      const res = await monitoramentoService.getIcsapsPacientes(
        municipioAtivo.id,
        dataInicio,
        dataFim,
        cid,
        filtrosParams,
      );
      setPacientes(res.pacientes || []);
    } catch {
      setPacientes([]);
    } finally {
      setLoadingPacientes(false);
    }
  };

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de ICSAPS.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const kpis = data?.kpis || {};
  const porGrupoIcsap: any[] = data?.porGrupoIcsap || [];
  const porGrupoCid: any[] = data?.porGrupoCid || [];
  const serie: any[] = (data?.serie || []).map((s: any) => ({
    ...s,
    competencia: s.competencia || s.mes,
  }));
  const porFaixaEtaria: any[] = data?.porFaixaEtaria || [];
  const porUnidade: any[] = data?.porUnidade || [];
  const porRaca: any[] = data?.porRaca || [];
  const opcoes = data?.filtrosOpcoes || {};

  const taxaIcsaps = Number(kpis.taxa_por_10k ?? kpis.taxa_icsaps ?? 0);
  const totalSensiveis = Number(kpis.total_icsaps ?? 0);
  const pctClinicas = Number(kpis.pct_icsaps_clinicas ?? kpis.pct_icsaps ?? 0);
  const custoTotal = Number(kpis.custo_total_icsaps ?? 0);

  const faixaMap: Record<string, { faixa_etaria: string; masculino: number; feminino: number; ordem: number }> = {};
  porFaixaEtaria.forEach((item: any) => {
    const faixa = item.faixa_etaria || 'N/A';
    if (!faixaMap[faixa]) {
      faixaMap[faixa] = { faixa_etaria: faixa, masculino: 0, feminino: 0, ordem: Number(item.faixa_ordem || 0) };
    }
    const total = Number(item.total || 0);
    const sexo = (item.sexo || '').toLowerCase();
    if (sexo === 'masculino' || sexo === 'm') {
      faixaMap[faixa].masculino = -total;
    } else if (sexo === 'feminino' || sexo === 'f') {
      faixaMap[faixa].feminino = total;
    }
  });
  const piramideData = Object.values(faixaMap).sort((a, b) => a.ordem - b.ordem);
  const maxValPiramide = piramideData.reduce((max, row) => Math.max(max, Math.abs(row.masculino), row.feminino), 0);
  const dominioPiramide = maxValPiramide > 0 ? Math.ceil(maxValPiramide * 1.1) : 100;

  const grupoIcsapChart = porGrupoIcsap.map((g: any) => ({
    grupo: g.grupo_nome || g.grupo,
    total: Number(g.total || 0),
  }));

  const serieKeys = serie.length > 0
    ? Object.keys(serie[0]).filter((k) => !['competencia', 'mes'].includes(k))
    : [];

  const anosDisponiveis = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i));

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            ICSAPS - Internacoes Sensiveis a APS
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
          {data?.fonte && (
            <Chip
              icon={<CloudDownloadIcon />}
              label={`Fonte: ${data.fonte.sistema} — ${data.fonte.origem}`}
              size="small"
              color="info"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <DateRangeSelector dataInicio={filtroInicio} dataFim={filtroFim} onChange={handleDateChange} />
      </Box>

      {/* Filtros RF-09.2 */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Filtros e segmentacao
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Segmento</InputLabel>
              <Select
                value={filtros.segmento}
                label="Segmento"
                onChange={(e) => setFiltros({ ...filtros, segmento: e.target.value })}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="no_municipio">Realizadas no municipio</MenuItem>
                <MenuItem value="residentes">Residentes do municipio</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Ano</InputLabel>
              <Select value={filtros.ano} label="Ano" onChange={(e) => setFiltros({ ...filtros, ano: e.target.value })}>
                <MenuItem value="">Todos</MenuItem>
                {anosDisponiveis.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select value={filtros.mes} label="Mes" onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={String(i + 1)}>{String(i + 1).padStart(2, '0')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Grupo de causa</InputLabel>
              <Select
                value={filtros.grupo_icsap}
                label="Grupo de causa"
                onChange={(e) => setFiltros({ ...filtros, grupo_icsap: e.target.value })}
              >
                <MenuItem value="">Todos</MenuItem>
                {(opcoes.grupos || []).map((g: any) => (
                  <MenuItem key={g.codigo} value={g.codigo}>{g.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>CID</InputLabel>
              <Select value={filtros.cid} label="CID" onChange={(e) => setFiltros({ ...filtros, cid: e.target.value })}>
                <MenuItem value="">Todos</MenuItem>
                {(opcoes.cids || []).map((c: any) => (
                  <MenuItem key={c.codigo} value={c.codigo}>{c.codigo} - {c.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estabelecimento</InputLabel>
              <Select
                value={filtros.codigo_unidade}
                label="Estabelecimento"
                onChange={(e) => setFiltros({ ...filtros, codigo_unidade: e.target.value })}
              >
                <MenuItem value="">Todos</MenuItem>
                {(opcoes.unidades || []).map((u: any) => (
                  <MenuItem key={u.codigo} value={String(u.codigo)}>{u.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sexo</InputLabel>
              <Select value={filtros.sexo} label="Sexo" onChange={(e) => setFiltros({ ...filtros, sexo: e.target.value })}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="masculino">Masculino</MenuItem>
                <MenuItem value="feminino">Feminino</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Faixa etaria</InputLabel>
              <Select
                value={filtros.faixa_etaria}
                label="Faixa etaria"
                onChange={(e) => setFiltros({ ...filtros, faixa_etaria: e.target.value })}
              >
                <MenuItem value="">Todas</MenuItem>
                {['< 1 ano', '1-4', '5-14', '15-24', '25-34', '35-44', '45-54', '55-64', '65-74', '75+'].map((f) => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Raca/cor</InputLabel>
              <Select value={filtros.raca} label="Raca/cor" onChange={(e) => setFiltros({ ...filtros, raca: e.target.value })}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="Branca">Branca</MenuItem>
                <MenuItem value="Parda">Parda</MenuItem>
                <MenuItem value="Preta">Preta</MenuItem>
                <MenuItem value="Amarela">Amarela</MenuItem>
                <MenuItem value="Indígena">Indigena</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleBuscar} fullWidth>
              Aplicar filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Taxa ICSAPS / 10k hab"
            valor={taxaIcsaps > 0 ? taxaIcsaps.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '—'}
            icone={<TrendingDownIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Total internacoes sensiveis"
            valor={totalSensiveis > 0 ? totalSensiveis.toLocaleString('pt-BR') : '—'}
            icone={<LocalHospitalIcon />}
            cor="#2D7D9A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="% ICSAPS / AIHs clinicas"
            valor={pctClinicas > 0 ? `${pctClinicas.toLocaleString('pt-BR')}%` : '—'}
            icone={<PercentIcon />}
            cor="#27AE60"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Custo total ICSAPS"
            valor={custoTotal > 0 ? custoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
            icone={<AttachMoneyIcon />}
            cor="#E74C3C"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Internacoes por grupo de causa (ICSAP)
            </Typography>
            {grupoIcsapChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(grupoIcsapChart.length * 36, 280)}>
                <BarChart data={grupoIcsapChart} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => v.toLocaleString('pt-BR')} />
                  <YAxis type="category" dataKey="grupo" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, _n) => fmtCount(v, 'Internacoes')} />
                  <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                    {grupoIcsapChart.map((_, i) => (
                      <Cell key={i} fill={CORES_GRUPOS[i % CORES_GRUPOS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Sem dados por grupo de causa.
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Ranking unidades hospitalares
            </Typography>
            {porUnidade.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(porUnidade.length * 36, 280)}>
                <BarChart
                  data={porUnidade.map((u: any) => ({
                    unidade: u.nome_unidade,
                    total: Number(u.total || 0),
                  }))}
                  layout="vertical"
                  margin={{ left: 10, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => v.toLocaleString('pt-BR')} />
                  <YAxis type="category" dataKey="unidade" width={160} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v, _n) => fmtCount(v, 'ICSAPS')} />
                  <Bar dataKey="total" fill="#2D7D9A" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Sem dados de ranking hospitalar.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Serie historica
        </Typography>
        {serie.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={serie}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="competencia" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => v.toLocaleString('pt-BR')} />
              <Tooltip formatter={fmtSerie} />
              <Legend formatter={(v) => SERIE_LABELS[v] || v} />
              {serieKeys.map((key, idx) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={CORES_GRUPOS[idx % CORES_GRUPOS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de serie historica.
          </Typography>
        )}
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Piramide etaria
            </Typography>
            {piramideData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(piramideData.length * 35, 300)}>
                <BarChart data={piramideData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    domain={[-dominioPiramide, dominioPiramide]}
                    tickFormatter={(v) => Math.abs(v).toLocaleString('pt-BR')}
                  />
                  <YAxis type="category" dataKey="faixa_etaria" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={fmtPiramide} />
                  <Legend />
                  <ReferenceLine x={0} stroke="#666" />
                  <Bar dataKey="masculino" fill="#2D7D9A" name="masculino" />
                  <Bar dataKey="feminino" fill="#E74C3C" name="feminino" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Sem dados de piramide etaria.
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Perfil por raca/cor
            </Typography>
            {porRaca.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={porRaca.map((r: any) => ({ raca: r.raca, total: Number(r.total || 0) }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="raca" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={70} />
                  <YAxis tickFormatter={(v) => v.toLocaleString('pt-BR')} />
                  <Tooltip formatter={(v, _n) => fmtCount(v, 'ICSAPS')} />
                  <Bar dataKey="total" fill="#8E44AD" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Sem dados de perfil por raca/cor.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela CID + rastreamento RF-09.6 */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Detalhamento por CID — clique para rastreamento individual
        </Typography>
        {porGrupoCid.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell sx={{ fontWeight: 600 }}>CID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descricao</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grupo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {porGrupoCid.map((row: any) => (
                  <React.Fragment key={row.cid}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => carregarPacientes(row.cid)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {cidSelecionado === row.cid ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.cid}</TableCell>
                      <TableCell>{row.nome_cid}</TableCell>
                      <TableCell>{row.grupo_nome || '—'}</TableCell>
                      <TableCell align="right">{Number(row.total || 0).toLocaleString('pt-BR')}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                        <Collapse in={cidSelecionado === row.cid} unmountOnExit>
                          <Box sx={{ py: 2, px: 1 }}>
                            {loadingPacientes ? (
                              <Typography variant="body2" color="text.secondary">Carregando pacientes...</Typography>
                            ) : pacientes.length > 0 ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Paciente</TableCell>
                                    <TableCell>Data internacao</TableCell>
                                    <TableCell>Hospital</TableCell>
                                    <TableCell align="right">Permanencia</TableCell>
                                    <TableCell align="right">Custo</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {pacientes.map((p: any, i: number) => (
                                    <TableRow key={i}>
                                      <TableCell>{p.nome_paciente}</TableCell>
                                      <TableCell>
                                        {p.data_internacao
                                          ? new Date(p.data_internacao).toLocaleDateString('pt-BR')
                                          : '—'}
                                      </TableCell>
                                      <TableCell>{p.nome_unidade}</TableCell>
                                      <TableCell align="right">{p.permanencia_dias ?? '—'} dias</TableCell>
                                      <TableCell align="right">
                                        {Number(p.valor_internacao || 0).toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                        })}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Nenhum paciente encontrado para este CID.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados detalhados de CID.
          </Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="icsaps"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoIcsaps;
